import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { NewsletterStatusPill } from '@/components/ui/NewsletterStatusPill'
import { ApiError } from '@/lib/api'
import {
  cancelCampaign,
  deleteCampaign,
  getCampaign,
  listSends,
  scheduleCampaign,
  sendNow,
  sendTest,
} from '@/lib/newsletter'
import type { NewsletterCampaign } from '@/lib/newsletter-types'
import {
  TimezoneConversionError,
  formatWallClockInZone,
  formatZonedTimePreview,
  parseDateTimeLocal,
  zonedTimeToUtc,
} from '@/lib/timezone'

const DEFAULT_TZ = 'America/Chicago'
const PAGE_SIZE = 100

function defaultScheduleInstant(): Date {
  // Default to "in 5 minutes, rounded up to next 5 minute mark" in the host's
  // wall clock. The modal then renders this in the selected timezone, not
  // in the browser's local zone.
  const now = new Date()
  const add5 = new Date(now.getTime() + 5 * 60 * 1000)
  add5.setSeconds(0, 0)
  add5.setMinutes(Math.ceil(add5.getMinutes() / 5) * 5)
  return add5
}

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) {
      return 'Your session may have expired. Please sign in again.'
    }
    if (err.status === 404) {
      return fallback
    }
    if (err.status >= 500) {
      return 'The server returned an error. Please try again in a moment.'
    }
    if (err.status >= 400) {
      return 'The request was rejected by the server. Please try again.'
    }
    return fallback
  }
  if (err instanceof Error) {
    if (err.name === 'TypeError') {
      return 'We could not reach the server. Check your connection and try again.'
    }
    return fallback
  }
  return fallback
}

function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404
}

export function NewsletterDetailPage() {
  const { newsletterId } = useParams<{ newsletterId: string }>()
  const queryClient = useQueryClient()

  const campaignQ = useQuery({
    queryKey: ['admin-newsletter-campaign', newsletterId],
    queryFn: () => getCampaign(newsletterId!),
    enabled: !!newsletterId,
  })

  const navigate = useNavigate()
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [sendsPage, setSendsPage] = useState(0)

  const sendsQ = useQuery({
    queryKey: ['admin-newsletter-sends', newsletterId, sendsPage],
    queryFn: () =>
      listSends(newsletterId!, { limit: PAGE_SIZE, offset: sendsPage * PAGE_SIZE }),
    enabled: !!newsletterId,
    // Keep the previous page's data visible while the next page loads, so
    // paging and 4s polls never blank the table mid-interaction.
    placeholderData: keepPreviousData,
    refetchInterval: (q) => {
      const data = q.state.data
      if (!data) return false
      const inFlight =
        campaignQ.data &&
        (campaignQ.data.status === 'scheduled' || campaignQ.data.status === 'sending')
      return inFlight ? 4000 : false
    },
  })

  // Reset pagination only when the operator switches campaigns. Polling and
  // refetch must not bounce the operator back to page 0.
  useEffect(() => {
    setSendsPage(0)
  }, [newsletterId])

  // If the underlying row count shrinks beneath the operator's current page
  // (e.g. deletions or a faster-than-expected read), snap back to the last
  // real page. Guarded by `sendsQ.data.total` so the effect only fires after
  // a known-good total — never on the initial pending state.
  const sendsTotal = sendsQ.data?.total
  useEffect(() => {
    if (typeof sendsTotal !== 'number') return
    const lastIndex = sendsTotal > 0 ? Math.floor((sendsTotal - 1) / PAGE_SIZE) : 0
    setSendsPage((current) => (current > lastIndex ? lastIndex : current))
  }, [sendsTotal])

  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [tz, setTz] = useState<string>(DEFAULT_TZ)
  const [whenLocal, setWhenLocal] = useState<string>(() =>
    formatWallClockInZone(defaultScheduleInstant(), DEFAULT_TZ),
  )
  const [scheduleFormError, setScheduleFormError] = useState<string | null>(null)
  // Captured separately from `scheduleMutation.error` so a non-timezone
  // failure (server, auth, network) stays visible inside the modal instead
  // of being reset before the next render can show it.
  const [scheduleApiError, setScheduleApiError] = useState<string | null>(null)
  // Token that increments on every open/close/reset. Captured in the
  // mutation callbacks so a late rejection from an earlier request cannot
  // repopulate `scheduleApiError` once the modal has been closed (or a new
  // attempt has been started by editing the inputs).
  const scheduleAttemptRef = useRef(0)

  const parsedParts = useMemo(() => parseDateTimeLocal(whenLocal), [whenLocal])

  const preview = useMemo(() => {
    if (!parsedParts) return null
    try {
      return formatZonedTimePreview(parsedParts, tz)
    } catch {
      return null
    }
  }, [parsedParts, tz])

  const isInPast = useMemo(() => {
    if (!preview) return false
    return preview.firesAtUtc.getTime() <= Date.now()
  }, [preview])

  const scheduleDisabledReason: string | null = (() => {
    if (!parsedParts) return 'Pick a valid date and time.'
    if (!preview) {
      return 'That time does not exist in the selected timezone (DST gap).'
    }
    if (isInPast) {
      return 'That time is already in the past.'
    }
    return null
  })()

  function clearScheduleState() {
    scheduleAttemptRef.current += 1
    setScheduleFormError(null)
    setScheduleApiError(null)
    scheduleMutation.reset()
  }

  const testMutation = useMutation({
    mutationFn: () => sendTest(newsletterId!),
    onSuccess: () => {
      // toast would go here in V2; keep it lean for now.
    },
  })

  const scheduleMutation = useMutation({
    mutationFn: () => {
      if (!parsedParts) {
        throw new TimezoneConversionError('Enter a date and time before scheduling.', 'invalid')
      }
      const utc = zonedTimeToUtc(parsedParts, tz)
      if (utc.getTime() <= Date.now()) {
        throw new TimezoneConversionError(
          'That time is already in the past. Pick a future time.',
          'invalid',
        )
      }
      const attemptToken = scheduleAttemptRef.current
      return scheduleCampaign(newsletterId!, {
        scheduled_for: Math.floor(utc.getTime() / 1000),
        timezone: tz,
      }).then((result) => {
        // Defensive: if the modal was closed or the inputs were edited
        // while the request was in flight, drop the result instead of
        // surfacing a success banner for a now-irrelevant attempt.
        if (scheduleAttemptRef.current !== attemptToken) {
          return null
        }
        return result
      })
    },
    onSuccess: (result) => {
      setScheduleFormError(null)
      setScheduleApiError(null)
      if (result === null) return
      setScheduleOpen(false)
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaign', newsletterId] })
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaigns'] })
    },
    onError: (err, _vars, _ctx) => {
      if (err instanceof TimezoneConversionError) {
        setScheduleFormError(err.message)
        return
      }
      if (scheduleAttemptRef.current === 0) {
        // We cancelled this attempt (modal closed or inputs edited). The
        // late rejection belongs to a previous attempt — drop it instead
        // of writing to scheduleApiError, otherwise the next open will
        // display a stale failure.
        scheduleMutation.reset()
        return
      }
      // Keep the modal open and surface the failure using the same
      // describeError vocabulary as the rest of the page so server,
      // authentication, and network errors are actionable.
      setScheduleApiError(
        describeError(err, 'Could not schedule the campaign. Please try again.'),
      )
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => cancelCampaign(newsletterId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaign', newsletterId] })
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaigns'] })
    },
  })

  const sendNowMutation = useMutation({
    mutationFn: () => sendNow(newsletterId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaign', newsletterId] })
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-sends', newsletterId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteCampaign(newsletterId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaigns'] })
    },
  })

  if (campaignQ.isLoading) {
    return <div className="flex items-center justify-center py-24"><LoadingSpinner size="lg" /></div>
  }

  if (campaignQ.isError) {
    // Genuine 404 from the server means the campaign really doesn't exist.
    if (isNotFoundError(campaignQ.error)) {
      return (
        <div className="py-12 text-center text-[var(--content-tertiary)]">
          Campaign not found.{' '}
          <Link to="/newsletters" className="text-[var(--brand)] hover:underline">
            Back to newsletters
          </Link>
        </div>
      )
    }

    // Any other failure (auth, network, 5xx) — show an actionable error,
    // never the misleading "Campaign not found" copy.
    return (
      <div className="space-y-4 py-12">
        <ErrorState
          title="Could not load this campaign"
          description={describeError(campaignQ.error, 'Something went wrong while loading the campaign.')}
          action={
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={() => campaignQ.refetch()} disabled={campaignQ.isFetching}>
                {campaignQ.isFetching ? 'Retrying…' : 'Try again'}
              </Button>
              <Link to="/newsletters">
                <Button variant="secondary">Back to newsletters</Button>
              </Link>
            </div>
          }
        />
      </div>
    )
  }

  const campaign: NewsletterCampaign | undefined = campaignQ.data
  if (!campaign) {
    // Defensive guard: success status but no payload — treat like a real 404
    // rather than silently rendering an empty page.
    return (
      <div className="py-12 text-center text-[var(--content-tertiary)]">
        Campaign not found.{' '}
        <Link to="/newsletters" className="text-[var(--brand)] hover:underline">
          Back to newsletters
        </Link>
      </div>
    )
  }

  // Defensive zero-fill: the server groups by status so absent buckets come
  // back as missing keys rather than zeros. `totalRecipients` must always be
  // a real number so the KPI card does not call toLocaleString on undefined.
  const rawSummary = sendsQ.data?.summary
  const summary = {
    queued: rawSummary?.queued ?? 0,
    sent: rawSummary?.sent ?? 0,
    failed: rawSummary?.failed ?? 0,
    skipped: rawSummary?.skipped ?? 0,
  }
  const totalRecipients = summary.queued + summary.sent + summary.failed + summary.skipped

  // `total` is the campaign-wide row count (server-computed). If the operator
  // is on a page that no longer exists, the useEffect above snaps them back
  // to the last real page; until that round-trip completes, `total` is the
  // source of truth for the visible range.
  const totalSends = sendsQ.data?.total ?? 0
  const lastPageIndex = totalSends > 0 ? Math.floor((totalSends - 1) / PAGE_SIZE) : 0
  const pageStart = totalSends === 0 ? 0 : sendsPage * PAGE_SIZE + 1
  const pageEnd = Math.min((sendsPage + 1) * PAGE_SIZE, totalSends)
  const showPagination = totalSends > PAGE_SIZE
  const canPrev = sendsPage > 0
  const canNext = (sendsPage + 1) * PAGE_SIZE < totalSends

  const canEdit = campaign.status === 'draft'
  const canSchedule = canEdit || campaign.status === 'scheduled' || campaign.status === 'canceled'
  const canCancel = campaign.status === 'scheduled'
  const canSendNow = canEdit || campaign.status === 'canceled'
  const canDelete = canEdit || campaign.status === 'canceled'
  const canTest = !!(campaign.html && campaign.subject)

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1">
            <Link to="/newsletters" className="text-sm text-[var(--content-tertiary)] hover:text-[var(--content)]">
              ← Newsletters
            </Link>
            <NewsletterStatusPill status={campaign.status} />
          </div>
          <h1 className="text-2xl font-heading font-semibold break-words">{campaign.subject || '(untitled)'}</h1>
          <p className="text-sm text-[var(--content-tertiary)] mt-1">
            To: <span className="capitalize">{campaign.audience === 'both' ? 'Free + Paid' : campaign.audience}</span>
            {' '}· From: <span className="font-mono text-xs">{campaign.from_name} &lt;{campaign.from_email}&gt;</span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && (
            <Link to={`/newsletters/${campaign.id}/edit`}>
              <Button variant="secondary">Edit design</Button>
            </Link>
          )}
          {canTest && (
            <Button
              variant="secondary"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
            >
              {testMutation.isPending ? 'Sending…' : 'Send test to josh'}
            </Button>
          )}
          {canSchedule && (
            <Button onClick={() => setScheduleOpen(true)}>Schedule</Button>
          )}
          {canCancel && (
            <Button
              variant="secondary"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              Cancel schedule
            </Button>
          )}
          {canSendNow && (
            <Button
              onClick={() => {
                if (confirm(`Send "${campaign.subject}" immediately to the ${campaign.audience} list?`)) {
                  sendNowMutation.mutate()
                }
              }}
              disabled={sendNowMutation.isPending}
            >
              {sendNowMutation.isPending ? 'Sending…' : 'Send now'}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              onClick={() => {
                if (confirm('Delete this campaign? This cannot be undone.')) {
                  deleteMutation.mutate(undefined, {
                    onSuccess: () => {
                      navigate('/newsletters')
                    },
                    onError: (err) => {
                      setDeleteError(String(err))
                    },
                  })
                }
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      {(testMutation.data || testMutation.error || sendNowMutation.data || sendNowMutation.error || deleteError || deleteMutation.error) && (
        <div className="text-sm space-y-1">
          {testMutation.data?.ok && (
            <p className="text-emerald-600">
              ✅ Test email sent to {testMutation.data.recipient ?? 'josh@voicescience.org'}.
            </p>
          )}
          {testMutation.error && (
            <p className="text-red-600">Test send failed: {String(testMutation.error)}</p>
          )}
          {sendNowMutation.data?.ok && sendNowMutation.data.result.ok && (
            <p className="text-emerald-600">
              ✅ Sent to {sendNowMutation.data.result.sent_count} recipients
              {sendNowMutation.data.result.failed_count > 0 &&
                ` (${sendNowMutation.data.result.failed_count} failed)`}.
            </p>
          )}
          {sendNowMutation.data?.ok && !sendNowMutation.data.result.ok && (
            <p className="text-red-600">
              Send failed: {sendNowMutation.data.result.error ?? 'unknown dispatch error'} — the
              campaign was marked <code>failed</code> and no recipients were delivered.
            </p>
          )}
          {sendNowMutation.error && (
            <p className="text-red-600">Send failed: {String(sendNowMutation.error)}</p>
          )}
          {deleteError && (
            <p className="text-red-600">Delete failed: {deleteError}</p>
          )}
          {deleteMutation.error && !deleteError && (
            <p className="text-red-600">Delete failed: {String(deleteMutation.error)}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KpiMini label="Sent" value={summary.sent} tone="emerald" />
        <KpiMini label="Queued" value={summary.queued} tone="blue" />
        <KpiMini label="Failed" value={summary.failed} tone="red" />
        <KpiMini label="Total recipients" value={totalRecipients} tone="slate" />
      </div>

      <Card>
        <CardHeader>
          <h2 className="font-heading font-semibold text-sm">Preview</h2>
        </CardHeader>
        <CardContent className="p-0">
          <iframe
            title="Email preview"
            srcDoc={campaign.html}
            // Sandbox the previewed HTML: it is server-persisted from client
            // input and `mj-raw` exposes author markup, so we render without
            // script execution or same-origin access.
            sandbox=""
            className="w-full min-h-[600px] bg-white"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-heading font-semibold text-sm">Delivery log</h2>
            {showPagination && (
              <div className="flex items-center gap-3 text-xs text-[var(--content-tertiary)] tabular-nums">
                <span>
                  Showing {pageStart.toLocaleString()}–{pageEnd.toLocaleString()} of{' '}
                  {totalSends.toLocaleString()}
                </span>
                {sendsQ.isFetching && (
                  <span className="inline-flex items-center gap-1" aria-live="polite">
                    <LoadingSpinner size="sm" />
                    <span>Refreshing…</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {sendsQ.isPending && !sendsQ.data ? (
            <div className="flex items-center justify-center py-8"><LoadingSpinner /></div>
          ) : sendsQ.isError ? (
            <ErrorState
              title="Could not load the delivery log"
              description={describeError(sendsQ.error, 'Something went wrong while loading deliveries.')}
              action={
                <Button onClick={() => sendsQ.refetch()} disabled={sendsQ.isFetching}>
                  {sendsQ.isFetching ? 'Retrying…' : 'Try again'}
                </Button>
              }
            />
          ) : totalSends === 0 ? (
            <p className="text-sm text-[var(--content-tertiary)] py-8 text-center">
              No deliveries recorded yet. Once you schedule or send this campaign, deliveries appear here.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto -mx-4">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-[var(--content-tertiary)]">
                    <tr>
                      <th className="px-4 py-2 font-medium">Email</th>
                      <th className="px-4 py-2 font-medium">List</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Sent at</th>
                      <th className="px-4 py-2 font-medium">Postmark ID</th>
                      <th className="px-4 py-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sendsQ.data?.items ?? []).map((s) => (
                      <tr key={s.id} className="border-t border-[var(--border)]">
                        <td className="px-4 py-2 font-mono text-xs">{s.email}</td>
                        <td className="px-4 py-2 capitalize text-[var(--content-secondary)]">{s.audience}</td>
                        <td className="px-4 py-2">
                          <span className={statusTone(s.status)}>{s.status}</span>
                        </td>
                        <td className="px-4 py-2 text-[var(--content-secondary)] tabular-nums">
                          {s.sent_at ? new Date(s.sent_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-[var(--content-tertiary)]">
                          {s.postmark_message_id ?? '—'}
                        </td>
                        <td className="px-4 py-2 text-red-600 text-xs">{s.error ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {showPagination && (
                <div className="flex items-center justify-between gap-3 mt-4 text-xs">
                  <span className="text-[var(--content-tertiary)] tabular-nums">
                    Page {sendsPage + 1} of {lastPageIndex + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSendsPage((p) => Math.max(0, p - 1))}
                      disabled={!canPrev || sendsQ.isFetching}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSendsPage((p) => p + 1)}
                      disabled={!canNext || sendsQ.isFetching}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Modal open={scheduleOpen} onClose={() => { setScheduleOpen(false); clearScheduleState() }} title="Schedule campaign">
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[var(--content-tertiary)]">When (in selected timezone)</span>
            <input
              type="datetime-local"
              value={whenLocal}
              onChange={(e) => {
                setWhenLocal(e.target.value)
                clearScheduleState()
              }}
              className="mt-1 block w-full rounded-md border border-[var(--border)] bg-[var(--surface-nested)] px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[var(--content-tertiary)]">Timezone</span>
            <select
              value={tz}
              onChange={(e) => {
                setTz(e.target.value)
                clearScheduleState()
              }}
              className="mt-1 block w-full rounded-md border border-[var(--border)] bg-[var(--surface-nested)] px-3 py-2"
            >
              <option value="America/Chicago">Central (Chicago)</option>
              <option value="America/New_York">Eastern (New York)</option>
              <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
              <option value="UTC">UTC</option>
            </select>
          </label>
          {preview ? (
            <div className="space-y-2">
              <p className="text-xs text-[var(--content-tertiary)]">
                Fires <span className="font-medium text-[var(--content)]">{preview.firesAtLocal}</span>
                {' '}→ {preview.utcString}
              </p>
              {preview.ambiguous && preview.ambiguityNote && (
                <p
                  role="status"
                  data-testid="ambiguity-notice"
                  className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                >
                  {preview.ambiguityNote}
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-[var(--content-tertiary)]">
              Pick a valid date and time in the selected timezone.
            </p>
          )}
          <p className="text-xs text-[var(--content-tertiary)]">
            The selected timezone controls when this campaign fires &mdash; not your
            browser clock. Daylight saving transitions are handled automatically using the
            IANA timezone database. Times that don&rsquo;t exist (the spring-forward gap)
            are rejected before the request is sent. When the fall-back overlap makes a
            wall-clock occur twice, scheduling uses the post-transition (standard-time)
            occurrence.
          </p>
          {scheduleFormError && (
            <p className="text-red-600 text-sm">{scheduleFormError}</p>
          )}
          {scheduleApiError && !scheduleFormError && (
            <p className="text-red-600 text-sm">{scheduleApiError}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setScheduleOpen(false); clearScheduleState() }}>Cancel</Button>
            <Button
              onClick={() => {
                scheduleAttemptRef.current += 1
                setScheduleFormError(null)
                setScheduleApiError(null)
                scheduleMutation.mutate()
              }}
              disabled={scheduleMutation.isPending || scheduleDisabledReason !== null}
              title={scheduleDisabledReason ?? undefined}
            >
              {scheduleMutation.isPending ? 'Scheduling…' : 'Schedule'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function statusTone(s: string): string {
  switch (s) {
    case 'sent':
      return 'text-emerald-600 font-semibold'
    case 'failed':
      return 'text-red-600 font-semibold'
    case 'queued':
      return 'text-blue-600'
    case 'skipped':
      return 'text-slate-500'
    default:
      return 'text-[var(--content-secondary)]'
  }
}

function KpiMini({ label, value, tone }: { label: string; value: number; tone: 'emerald' | 'blue' | 'red' | 'slate' }) {
  const color =
    tone === 'emerald'
      ? 'text-emerald-600'
      : tone === 'blue'
        ? 'text-blue-600'
        : tone === 'red'
          ? 'text-red-600'
          : 'text-[var(--content)]'
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs uppercase tracking-wider text-[var(--content-tertiary)] mb-1">{label}</p>
        <p className={`text-2xl font-heading font-semibold tabular-nums ${color}`}>{value.toLocaleString()}</p>
      </CardContent>
    </Card>
  )
}
